/**
 * Shared color theme for ticket statuses.
 *
 * Single source of truth for how a status is *painted* across the app. Any
 * component that renders a status pill, badge, Kanban column, dashboard
 * bucket or map marker MUST resolve its colors through this module — do not
 * inline hex values in individual pages. That way, when an admin changes a
 * status color in Pengaturan Aplikasi, the whole app follows.
 *
 * Palette keys are intentionally a small, hand-curated set that pairs well
 * with the existing shadcn/Tailwind palette. Both the backend Pydantic
 * validator (`_ALLOWED_COLORS` in server.py) and the AppSettings swatches
 * pull from `PALETTE_KEYS` — keep the three in sync when adding a color.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Palette tokens per color. `bg` is the soft tint, `fg` the readable text,
 *  `border` the subtle outline, `accent` the strong dot/bar. */
export const STATUS_PALETTE = {
  blue:   { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE", accent: "#0055FF" },
  indigo: { bg: "#E0E7FF", fg: "#3730A3", border: "#C7D2FE", accent: "#4338CA" },
  violet: { bg: "#F3E8FF", fg: "#6B21A8", border: "#E9D5FF", accent: "#7C3AED" },
  purple: { bg: "#FAE8FF", fg: "#86198F", border: "#F5D0FE", accent: "#A21CAF" },
  cyan:   { bg: "#CFFAFE", fg: "#155E75", border: "#A5F3FC", accent: "#0891B2" },
  teal:   { bg: "#CCFBF1", fg: "#115E59", border: "#99F6E4", accent: "#0D9488" },
  green:  { bg: "#D1FAE5", fg: "#065F46", border: "#A7F3D0", accent: "#059669" },
  amber:  { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", accent: "#D97706" },
  orange: { bg: "#FFEDD5", fg: "#9A3412", border: "#FED7AA", accent: "#EA580C" },
  red:    { bg: "#FEE2E2", fg: "#991B1B", border: "#FECACA", accent: "#DC2626" },
  rose:   { bg: "#FFE4E6", fg: "#9F1239", border: "#FECDD3", accent: "#E11D48" },
  slate:  { bg: "#F3F4F6", fg: "#4B5563", border: "#E5E7EB", accent: "#6B7280" },
};

/** Ordered list used by the palette-picker UI. */
export const PALETTE_KEYS = [
  "blue", "indigo", "violet", "purple", "cyan", "teal",
  "green", "amber", "orange", "red", "rose", "slate",
];

/** Fallback tint per canonical status code (used when a legacy row has no
 *  `color` field yet). Mirrors the old hardcoded per-page maps so nothing
 *  looks jarring until an admin explicitly picks a new color. */
const DEFAULT_COLOR_FOR_CODE = {
  OPEN: "amber",
  SCHEDULED: "violet",
  PROSES: "blue",
  SURVEY: "cyan",
  WAITING: "orange",
  RESCHEDULE: "indigo",
  DONE: "green",
  CANCEL: "red",
};

/** Resolve a palette key to its token bundle. Unknown/missing keys collapse
 *  to slate so the UI never renders an "invisible" pill. */
export function themeForColor(colorKey) {
  return STATUS_PALETTE[colorKey] || STATUS_PALETTE.slate;
}

/**
 * Resolve the theme for a given status code, preferring the color stored on
 * the master row. When `statusList` hasn't loaded yet (initial paint), fall
 * back to the canonical DEFAULT_COLOR_FOR_CODE map so the UI is never
 * blank/slate for legacy codes.
 *
 * @param {string} code Ticket status code, e.g. "OPEN".
 * @param {Array<{code:string,color?:string}>} statusList  Master rows from /api/reference.
 */
export function themeForStatus(code, statusList) {
  const master = Array.isArray(statusList)
    ? statusList.find((s) => s.code === code)
    : null;
  const color = master?.color || DEFAULT_COLOR_FOR_CODE[code] || "slate";
  return themeForColor(color);
}

/** Just the resolved palette key — useful when a caller needs to persist it
 *  or pass it to an <option>-like child. */
export function colorForStatus(code, statusList) {
  const master = Array.isArray(statusList)
    ? statusList.find((s) => s.code === code)
    : null;
  return master?.color || DEFAULT_COLOR_FOR_CODE[code] || "slate";
}

// --- Reference cache ------------------------------------------------------
// Fetched once per page-load, then re-used across every hook consumer so we
// don't hammer /api/reference from every badge on the page.
let _cachedStatuses = null;
let _cachedPromise = null;
const _subscribers = new Set();

function _notify() { for (const fn of _subscribers) fn(_cachedStatuses); }

async function _fetchStatuses() {
  if (!_cachedPromise) {
    _cachedPromise = api.get("/reference")
      .then(({ data }) => { _cachedStatuses = data?.status || []; _notify(); return _cachedStatuses; })
      .catch((err) => { _cachedPromise = null; throw err; });
  }
  return _cachedPromise;
}

/** Invalidate the cache — call this after saving master status changes so
 *  subsequent renders pick up the new colors without a full page refresh. */
export function invalidateStatusReference() {
  _cachedStatuses = null;
  _cachedPromise = null;
  _notify();
}

/** React hook — returns the master status list, subscribing to any refresh
 *  triggered by `invalidateStatusReference()`. Empty array until first fetch. */
export function useStatusReference() {
  const [statuses, setStatuses] = useState(_cachedStatuses || []);
  useEffect(() => {
    let alive = true;
    _subscribers.add(setStatuses);
    if (!_cachedStatuses) {
      _fetchStatuses().then((list) => { if (alive) setStatuses(list); }).catch(() => {});
    }
    return () => { alive = false; _subscribers.delete(setStatuses); };
  }, []);
  return statuses;
}
